import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { clientsAPI, tryonAPI, fabricsAPI } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  User, UserPlus, Camera, Search, ChevronRight, ChevronLeft, Upload,
  Loader2, Shirt, Check, X, Download, MessageCircle, RefreshCw,
  AlertCircle, Eye, Save, Copy,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GARMENT_TYPES = [
  { value: 'suit', label: 'Suit (Jacket + Trousers)', icon: '👔', desc: 'Matching jacket & trousers' },
  { value: 'jacket_only', label: 'Jacket / Blazer Only', icon: '🧥', desc: 'Just the jacket' },
  { value: 'trousers', label: 'Trousers', icon: '👖', desc: 'Bespoke trousers' },
  { value: 'shirt', label: 'Shirt', icon: '👕', desc: 'Formal or casual' },
  { value: 'sherwani', label: 'Sherwani', icon: '🪭', desc: 'Wedding & festive' },
  { value: 'bandhgala', label: 'Bandhgala', icon: '🎖️', desc: 'Nehru/Bandhgala' },
  { value: 'waistcoat', label: 'Waistcoat', icon: '🎩', desc: 'Formal waistcoat' },
  { value: 'kurta', label: 'Kurta', icon: '👘', desc: 'Traditional kurta' },
  { value: 'full_look', label: 'Full Look (Top + Bottom)', icon: '✨', desc: 'Different top & bottom fabrics' },
  { value: 'garment_photo', label: 'Use Garment Photo', icon: '📸', desc: 'Upload a garment photo' },
];

export function TryOnFabricPanel({ fabric, isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [clientMode, setClientMode] = useState(null); // 'existing', 'new', 'quick'
  
  // Existing client state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPhoto, setClientPhoto] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // New client state
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', phone: '' });
  const [newClientPhoto, setNewClientPhoto] = useState(null);
  const [newClientPhotoPreview, setNewClientPhotoPreview] = useState(null);
  
  // Quick try state
  const [quickPhoto, setQuickPhoto] = useState(null);
  const [quickPhotoPreview, setQuickPhotoPreview] = useState(null);
  
  // Garment state
  const [selectedGarment, setSelectedGarment] = useState(null);
  
  // Garment Photo mode state
  const [garmentPhoto, setGarmentPhoto] = useState(null);
  const [garmentPhotoPreview, setGarmentPhotoPreview] = useState(null);
  const garmentPhotoRef = useRef(null);
  
  // Full Look (multi-garment) state
  const [bottomGarment, setBottomGarment] = useState('trousers');
  const [bottomFabric, setBottomFabric] = useState(null);
  const [bottomFabrics, setBottomFabrics] = useState([]);
  const [bottomFabricSearch, setBottomFabricSearch] = useState('');
  
  // Suit mode state
  const [trouserConstruction, setTrouserConstruction] = useState({
    fit: 'slim', pleat: 'flat front', break_style: 'half',
  });
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  
  // Saving state
  const [saving, setSaving] = useState(false);
  const [showSaveToClientDialog, setShowSaveToClientDialog] = useState(false);
  const [saveClientForm, setSaveClientForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [savingToClient, setSavingToClient] = useState(false);
  
  const fileInputRef = useRef(null);
  const quickFileInputRef = useRef(null);

  // Reset state when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setClientMode(null);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedClient(null);
      setClientPhoto(null);
      setNewClient({ first_name: '', last_name: '', phone: '' });
      setNewClientPhoto(null);
      setNewClientPhotoPreview(null);
      setQuickPhoto(null);
      setQuickPhotoPreview(null);
      setSelectedGarment(null);
      setGarmentPhoto(null);
      setGarmentPhotoPreview(null);
      setBottomGarment('trousers');
      setBottomFabric(null);
      setBottomFabrics([]);
      setBottomFabricSearch('');
      setTrouserConstruction({ fit: 'slim', pleat: 'flat front', break_style: 'half' });
      setGenerating(false);
      setGeneratedImage(null);
      setGenerationError(null);
    }
  }, [isOpen]);

  // Search clients
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await clientsAPI.list({ search: searchQuery, limit: 10 });
        setSearchResults(res.data.clients || []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load client photo when client is selected
  useEffect(() => {
    if (selectedClient?.id) {
      loadClientPhoto(selectedClient.id);
    }
  }, [selectedClient]);

  const loadClientPhoto = async (clientId) => {
    try {
      const res = await clientsAPI.listPhotos(clientId);
      const photos = res.data || [];
      // Get most recent suitable photo
      const suitable = photos.find(p => p.tryon_suitable) || photos[0];
      if (suitable) {
        const url = suitable.photo_url || suitable.url;
        setClientPhoto(url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url);
      } else {
        setClientPhoto(null);
      }
    } catch (err) {
      setClientPhoto(null);
    }
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    const preview = URL.createObjectURL(file);
    
    if (type === 'new') {
      setNewClientPhoto(file);
      setNewClientPhotoPreview(preview);
    } else if (type === 'quick') {
      setQuickPhoto(file);
      setQuickPhotoPreview(preview);
    } else if (type === 'existing') {
      // Upload to existing client
      uploadPhotoToClient(file);
    }
  };

  const uploadPhotoToClient = async (file) => {
    if (!selectedClient?.id) return;
    
    try {
      toast.loading('Uploading photo...');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await clientsAPI.uploadPhoto(selectedClient.id, formData);
      const url = res.data?.photo_url || res.data?.url;
      setClientPhoto(url?.startsWith('/api') ? `${BACKEND_URL}${url}` : url);
      toast.dismiss();
      toast.success('Photo uploaded');
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to upload photo');
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.first_name || !newClient.last_name || !newClient.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!newClientPhoto) {
      toast.error('Please upload a photo');
      return;
    }
    
    try {
      toast.loading('Creating client...');
      
      // Create client
      const clientRes = await clientsAPI.create({
        first_name: newClient.first_name,
        last_name: newClient.last_name,
        phone: newClient.phone,
      });
      
      const createdClient = clientRes.data;
      
      // Upload photo
      const formData = new FormData();
      formData.append('file', newClientPhoto);
      await clientsAPI.uploadPhoto(createdClient.id, formData);
      
      toast.dismiss();
      toast.success('Client created');
      
      // Set as selected client
      setSelectedClient(createdClient);
      setClientPhoto(newClientPhotoPreview);
      setClientMode('existing');
      setStep(2);
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to create client');
    }
  };

  const canProceedToStep2 = () => {
    if (clientMode === 'existing') {
      return selectedClient && clientPhoto;
    } else if (clientMode === 'new') {
      return newClient.first_name && newClient.last_name && newClient.phone && newClientPhoto;
    } else if (clientMode === 'quick') {
      return quickPhoto;
    }
    return false;
  };

  const proceedToStep2 = () => {
    if (clientMode === 'new') {
      handleCreateClient();
    } else {
      setStep(2);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationError(null);
    
    try {
      // Prepare the photo
      let photoData = null;
      let clientId = null;
      
      if (clientMode === 'quick' && quickPhoto) {
        // Convert file to base64
        const reader = new FileReader();
        photoData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(quickPhoto);
        });
      } else if (selectedClient) {
        clientId = selectedClient.id;
      }
      
      // Build fabric description
      const fabricDesc = [
        fabric.colour_primary,
        fabric.pattern_type,
        fabric.composition,
        fabric.mill_name,
      ].filter(Boolean).join(' ');
      
      // Build request payload based on mode
      const payload = {
        client_id: clientId,
        garment_type: selectedGarment,
        fabric_description: fabricDesc,
        fabric_id: fabric.id,
        photo_base64: photoData,
        source: 'fabric_browse',
      };
      
      // Garment Photo mode: attach garment photo
      if (selectedGarment === 'garment_photo' && garmentPhoto) {
        const gReader = new FileReader();
        const garmentBase64 = await new Promise((resolve, reject) => {
          gReader.onload = () => resolve(gReader.result);
          gReader.onerror = reject;
          gReader.readAsDataURL(garmentPhoto);
        });
        payload.garment_photo_base64 = garmentBase64;
      }
      
      // Suit mode: attach trouser construction
      if (selectedGarment === 'suit') {
        payload.trouser_construction = trouserConstruction;
      }
      
      // Full Look mode: attach bottom garment + fabric
      if (selectedGarment === 'full_look') {
        payload.bottom_garment_type = bottomGarment;
        payload.bottom_fabric_id = bottomFabric?.id;
        payload.bottom_fabric_description = bottomFabric ? [
          bottomFabric.colour_primary, bottomFabric.pattern_type, bottomFabric.composition,
        ].filter(Boolean).join(' ') : '';
        payload.construction_details = {
          ...payload.construction_details,
          top_garment_type: 'jacket', // default top
        };
      }
      
      // Call API - images are auto-saved by backend for non-quick-try mode
      const res = await tryonAPI.generate(payload);
      
      setGeneratedImage(res.data);
      setStep(4);
      
      // Show success toast with auto-save confirmation for non-quick-try mode
      if (clientId && res.data?.auto_saved) {
        const clientName = selectedClient?.first_name || 'Client';
        toast.success(`Look saved to ${clientName}'s gallery`);
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Generation failed';
      setGenerationError(errMsg);
      toast.error(errMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveToGallery = async () => {
    if (!generatedImage?.id || !selectedClient?.id) return;
    
    setSaving(true);
    try {
      await tryonAPI.saveToGallery(generatedImage.id, selectedClient.id);
      toast.success('Saved to client gallery');
      onSuccess?.();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedImage?.image_url && !generatedImage?.generated_image_url) return;
    
    const url = generatedImage.image_url || generatedImage.generated_image_url;
    
    try {
      if (url.startsWith('data:')) {
        const response = await fetch(url);
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `tryon-${fabric.internal_code}-${selectedGarment}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `tryon-${fabric.internal_code}.png`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      toast.success('Downloaded');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleShare = async () => {
    const clientName = selectedClient?.first_name || 'there';
    const garment = selectedGarment?.replace(/_/g, ' ') || 'garment';
    const fabricName = fabric.internal_code;
    
    const message = `Hi ${clientName}! Here's a preview of your ${garment} in ${fabricName}. What do you think? - Herringbone & Sui`;
    
    await navigator.clipboard.writeText(message);
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    handleDownload();
    toast.success('Message copied! Attach the downloaded image in WhatsApp.');
  };

  const handleSaveToNewClient = async () => {
    if (!saveClientForm.first_name || !saveClientForm.last_name || !saveClientForm.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSavingToClient(true);
    try {
      // Create client
      const clientRes = await clientsAPI.create({
        first_name: saveClientForm.first_name,
        last_name: saveClientForm.last_name,
        phone: saveClientForm.phone,
      });
      
      const newClient = clientRes.data;
      
      // Save the try-on image to the new client
      const imageUrl = generatedImage?.image_url || generatedImage?.generated_image_url;
      await tryonAPI.saveQuickTryToClient({
        image_data: imageUrl,
        client_id: newClient.id,
        garment_type: selectedGarment,
        fabric_id: fabric.id,
        fabric_code: fabric.internal_code,
        fabric_description: generatedImage?.fabric_description || '',
      });
      
      toast.success(`Saved to ${saveClientForm.first_name}'s gallery`);
      setShowSaveToClientDialog(false);
      setSaveClientForm({ first_name: '', last_name: '', phone: '' });
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingToClient(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
        Select or add a client to try on <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>{fabric.internal_code}</span>
      </p>
      
      {/* Mode selection */}
      {!clientMode && (
        <div className="grid gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-[var(--brand-gold)]"
            onClick={() => setClientMode('existing')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                <User className="w-6 h-6" style={{ color: 'var(--brand-navy)' }} />
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>Existing Client</p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Search for a client with photos</p>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-[var(--brand-gold)]"
            onClick={() => setClientMode('new')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(197,165,90,0.12)' }}>
                <UserPlus className="w-6 h-6" style={{ color: 'var(--brand-gold)' }} />
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>Quick Add Client</p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Create new client with photo</p>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-[var(--brand-gold)]"
            onClick={() => setClientMode('quick')}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                <Camera className="w-6 h-6" style={{ color: 'var(--brand-navy)' }} />
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--brand-navy)' }}>Quick Try — No Client</p>
                <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Just upload a photo for preview</p>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: 'var(--ink-muted)' }} />
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Existing client search */}
      {clientMode === 'existing' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setClientMode(null)} className="gap-1 text-xs">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or phone..."
              className="pl-10 h-11 rounded-lg"
            />
          </div>
          
          {searchLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-gold)' }} />
            </div>
          )}
          
          {searchResults.length > 0 && (
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {searchResults.map(client => (
                  <div
                    key={client.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedClient?.id === client.id 
                        ? 'border-[var(--brand-gold)] bg-[rgba(197,165,90,0.08)]' 
                        : 'border-border/60 hover:border-border'
                    }`}
                    onClick={() => setSelectedClient(client)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                        <User className="w-5 h-5" style={{ color: 'var(--brand-navy)' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: 'var(--brand-navy)' }}>
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{client.phone}</p>
                      </div>
                      {selectedClient?.id === client.id && (
                        <Check className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          
          {selectedClient && (
            <div className="space-y-3 p-4 rounded-xl border border-border/60" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                Selected: {selectedClient.first_name} {selectedClient.last_name}
              </p>
              
              {clientPhoto ? (
                <div className="relative aspect-[3/4] w-32 rounded-lg overflow-hidden border border-border/60">
                  <img src={clientPhoto} alt="Client" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-amber-800 font-medium">No suitable photo found</p>
                    <p className="text-xs text-amber-700 mt-0.5">Upload a full-length photo to continue</p>
                    <input type="file" ref={fileInputRef} accept="image/*" onChange={(e) => handleFileUpload(e, 'existing')} className="hidden" />
                    <Button size="sm" variant="outline" className="mt-2 gap-1 text-xs" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="w-3 h-3" /> Upload Photo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* New client form */}
      {clientMode === 'new' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setClientMode(null)} className="gap-1 text-xs">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">First Name *</Label>
              <Input
                value={newClient.first_name}
                onChange={(e) => setNewClient(p => ({ ...p, first_name: e.target.value }))}
                placeholder="John"
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Last Name *</Label>
              <Input
                value={newClient.last_name}
                onChange={(e) => setNewClient(p => ({ ...p, last_name: e.target.value }))}
                placeholder="Smith"
                className="h-10"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Phone *</Label>
            <Input
              value={newClient.phone}
              onChange={(e) => setNewClient(p => ({ ...p, phone: e.target.value }))}
              placeholder="+91 98765 43210"
              className="h-10"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label className="text-xs">Photo *</Label>
            <input type="file" ref={fileInputRef} accept="image/*" onChange={(e) => handleFileUpload(e, 'new')} className="hidden" />
            {newClientPhotoPreview ? (
              <div className="relative aspect-[3/4] w-32 rounded-lg overflow-hidden border border-border/60">
                <img src={newClientPhotoPreview} alt="Preview" className="w-full h-full object-cover" />
                <button
                  onClick={() => { setNewClientPhoto(null); setNewClientPhotoPreview(null); }}
                  className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-32 rounded-lg border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-2 hover:border-[var(--brand-gold)] transition-colors"
              >
                <Camera className="w-6 h-6" style={{ color: 'var(--ink-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Take or upload photo</span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Quick try */}
      {clientMode === 'quick' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setClientMode(null)} className="gap-1 text-xs">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Upload a full-length photo for a quick preview. No client record will be created.
          </p>
          
          <input type="file" ref={quickFileInputRef} accept="image/*" onChange={(e) => handleFileUpload(e, 'quick')} className="hidden" />
          {quickPhotoPreview ? (
            <div className="relative aspect-[3/4] w-40 mx-auto rounded-lg overflow-hidden border border-border/60">
              <img src={quickPhotoPreview} alt="Preview" className="w-full h-full object-cover" />
              <button
                onClick={() => { setQuickPhoto(null); setQuickPhotoPreview(null); }}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => quickFileInputRef.current?.click()}
              className="w-full h-40 rounded-xl border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-3 hover:border-[var(--brand-gold)] transition-colors"
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.06)' }}>
                <Camera className="w-7 h-7" style={{ color: 'var(--brand-navy)' }} />
              </div>
              <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Take or upload photo</span>
            </button>
          )}
        </div>
      )}
      
      {/* Proceed button */}
      {clientMode && (
        <div className="pt-2">
          <Button
            onClick={proceedToStep2}
            disabled={!canProceedToStep2()}
            className="w-full h-11 rounded-xl gap-2"
            style={{ backgroundColor: 'var(--brand-gold)' }}
          >
            {clientMode === 'new' ? 'Create & Continue' : 'Continue'}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="gap-1 text-xs">
        <ChevronLeft className="w-4 h-4" /> Back
      </Button>
      
      <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>Select Garment Type</p>
      
      <div className="grid grid-cols-2 gap-2">
        {GARMENT_TYPES.map(g => (
          <Card
            key={g.value}
            className={`cursor-pointer transition-all ${
              selectedGarment === g.value
                ? 'border-2 border-[var(--brand-gold)] shadow-md'
                : 'border-2 border-transparent hover:border-border'
            }`}
            onClick={() => setSelectedGarment(g.value)}
            data-testid={`garment-type-${g.value}`}
          >
            <CardContent className="p-2.5 flex items-center gap-2">
              <span className="text-xl">{g.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: 'var(--brand-navy)' }}>{g.label}</p>
                {g.desc && <p className="text-[9px] truncate" style={{ color: 'var(--ink-muted)' }}>{g.desc}</p>}
              </div>
              {selectedGarment === g.value && (
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--brand-gold)' }} />
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Garment Photo mode: upload area */}
      {selectedGarment === 'garment_photo' && (
        <div className="p-3 rounded-xl border-2 border-dashed" style={{ borderColor: 'var(--brand-gold)' }} data-testid="garment-photo-upload">
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--brand-navy)' }}>
            Have a photo of a garment? Upload it to try on.
          </p>
          <input type="file" accept="image/*" className="hidden" ref={garmentPhotoRef}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setGarmentPhoto(file);
                const reader = new FileReader();
                reader.onload = () => setGarmentPhotoPreview(reader.result);
                reader.readAsDataURL(file);
              }
            }}
          />
          {garmentPhotoPreview ? (
            <div className="relative">
              <img src={garmentPhotoPreview} alt="Garment" className="w-full h-28 object-contain rounded-lg" />
              <button onClick={() => { setGarmentPhoto(null); setGarmentPhotoPreview(null); }}
                className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <Button variant="outline" className="w-full gap-2 h-9 text-xs" onClick={() => garmentPhotoRef.current?.click()}>
              <Upload className="w-4 h-4" /> Upload or Take Photo of Garment
            </Button>
          )}
        </div>
      )}
      
      {/* Suit mode: trouser construction details */}
      {selectedGarment === 'suit' && (
        <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }} data-testid="suit-trouser-options">
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--brand-navy)' }}>Trouser Details (same fabric)</p>
          <div className="grid grid-cols-3 gap-2">
            {[{label:'Fit',key:'fit',opts:['slim','regular','relaxed']},
              {label:'Pleat',key:'pleat',opts:['flat front','single pleat','double pleat']},
              {label:'Break',key:'break_style',opts:['no break','quarter','half','full']},
            ].map(item => (
              <div key={item.key}>
                <Label className="text-[9px]">{item.label}</Label>
                <Select value={trouserConstruction[item.key]} onValueChange={v => setTrouserConstruction(p => ({...p, [item.key]: v}))}>
                  <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {item.opts.map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Full Look mode: bottom garment + fabric selection */}
      {selectedGarment === 'full_look' && (
        <div className="space-y-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }} data-testid="full-look-options">
          <div>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--brand-navy)' }}>
              Top: Using current fabric ({fabric?.fabric_code || fabric?.fabric_name || 'selected'})
            </p>
          </div>
          <div>
            <Label className="text-xs">Bottom Garment Type</Label>
            <Select value={bottomGarment} onValueChange={setBottomGarment}>
              <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trousers">Trousers</SelectItem>
                <SelectItem value="pyjama">Pyjama</SelectItem>
                <SelectItem value="churidar">Churidar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bottom Fabric (different from top)</Label>
            <Input placeholder="Search fabric..." value={bottomFabricSearch} className="h-8 text-xs mt-1"
              onChange={e => {
                setBottomFabricSearch(e.target.value);
                if (e.target.value.length >= 2) {
                  fabricsAPI.list({ search: e.target.value, limit: 5 }).then(r => setBottomFabrics(r.data?.fabrics || []));
                }
              }}
            />
            {bottomFabrics.length > 0 && !bottomFabric && (
              <div className="border rounded-lg mt-1 max-h-[100px] overflow-auto">
                {bottomFabrics.map(f => (
                  <div key={f.id} className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs flex items-center gap-2"
                    onClick={() => { setBottomFabric(f); setBottomFabrics([]); }}>
                    {f.swatch_image_url && <img src={f.swatch_image_url} className="w-6 h-6 rounded object-cover" alt="" />}
                    <span>{f.fabric_code || f.fabric_name}</span>
                  </div>
                ))}
              </div>
            )}
            {bottomFabric && (
              <div className="mt-1 flex items-center gap-2 p-1.5 rounded-lg border">
                {bottomFabric.swatch_image_url && <img src={bottomFabric.swatch_image_url} className="w-8 h-8 rounded object-cover" alt="" />}
                <span className="text-xs font-medium flex-1">{bottomFabric.fabric_code || bottomFabric.fabric_name}</span>
                <button onClick={() => setBottomFabric(null)} className="text-red-500"><X className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="pt-2">
        <Button
          onClick={() => setStep(3)}
          disabled={!selectedGarment || 
            (selectedGarment === 'garment_photo' && !garmentPhoto) ||
            (selectedGarment === 'full_look' && !bottomFabric)}
          className="w-full h-11 rounded-xl gap-2"
          style={{ backgroundColor: 'var(--brand-gold)' }}
        >
          Continue <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => setStep(2)} className="gap-1 text-xs">
        <ChevronLeft className="w-4 h-4" /> Back
      </Button>
      
      <div className="p-4 rounded-xl border border-border/60" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
        <p className="text-sm font-medium mb-3" style={{ color: 'var(--brand-navy)' }}>Summary</p>
        <div className="space-y-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
          <div className="flex justify-between">
            <span>Fabric:</span>
            <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>{fabric.internal_code}</span>
          </div>
          <div className="flex justify-between">
            <span>Garment:</span>
            <span className="font-medium capitalize" style={{ color: 'var(--brand-navy)' }}>{selectedGarment?.replace(/_/g, ' ')}</span>
          </div>
          {selectedClient && (
            <div className="flex justify-between">
              <span>Client:</span>
              <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>{selectedClient.first_name} {selectedClient.last_name}</span>
            </div>
          )}
          {clientMode === 'quick' && (
            <div className="flex justify-between">
              <span>Mode:</span>
              <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>Quick Preview</span>
            </div>
          )}
        </div>
      </div>
      
      {generationError && (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-800">{generationError}</p>
        </div>
      )}
      
      <Button
        onClick={handleGenerate}
        disabled={generating}
        className="w-full h-12 rounded-xl gap-2 text-base"
        style={{ backgroundColor: 'var(--brand-gold)' }}
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Creating your look...
          </>
        ) : (
          <>
            <Shirt className="w-5 h-5" />
            Generate Try-On
          </>
        )}
      </Button>
    </div>
  );

  const renderStep4 = () => {
    const imageUrl = generatedImage?.image_url || generatedImage?.generated_image_url;
    const isQuickTry = clientMode === 'quick';
    const usedSwatch = generatedImage?.used_swatch_image;
    const swatchWarning = generatedImage?.swatch_warning;
    
    return (
      <div className="space-y-4">
        <p className="text-sm font-medium text-center" style={{ color: 'var(--brand-navy)' }}>
          Your Try-On Preview
        </p>
        
        {/* Auto-save confirmation for client mode */}
        {!isQuickTry && selectedClient && generatedImage?.auto_saved && (
          <div className="p-3 rounded-lg border border-green-200 bg-green-50 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-800">
              Automatically saved to <span className="font-semibold">{selectedClient.first_name}'s</span> gallery
            </p>
          </div>
        )}
        
        {/* Swatch usage indicator */}
        {usedSwatch && (
          <div className="p-2 rounded-lg border border-blue-200 bg-blue-50 flex items-center gap-2">
            <Check className="w-3 h-3 text-blue-600 flex-shrink-0" />
            <p className="text-[10px] text-blue-800">
              Generated with fabric swatch image for exact pattern matching
            </p>
          </div>
        )}
        
        {/* Warning if no swatch was used */}
        {swatchWarning && (
          <div className="p-2 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-2">
            <AlertCircle className="w-3 h-3 text-amber-600 flex-shrink-0" />
            <p className="text-[10px] text-amber-800">
              {swatchWarning}
            </p>
          </div>
        )}
        
        {imageUrl ? (
          <div className="aspect-[3/4] w-full max-w-[280px] mx-auto rounded-xl overflow-hidden border border-border/60 shadow-lg">
            <img src={imageUrl} alt="Try-on result" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[3/4] w-full max-w-[280px] mx-auto rounded-xl border border-border/60 flex items-center justify-center">
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Image loading...</p>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-2">
          {/* For Quick Try mode, show "Save to New Client" button */}
          {isQuickTry && (
            <Button 
              onClick={() => setShowSaveToClientDialog(true)} 
              className="gap-2 h-10 text-xs col-span-2" 
              style={{ backgroundColor: 'var(--brand-gold)' }}
              data-testid="save-to-new-client-btn"
            >
              <UserPlus className="w-4 h-4" />
              Save to New Client
            </Button>
          )}
          <Button onClick={handleShare} variant="outline" className="gap-2 h-10 text-xs"
            style={{ borderColor: '#25D366', color: '#25D366' }} data-testid="tryon-whatsapp-btn">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </Button>
          <Button onClick={async () => {
            const url = generatedImage?.image_url || generatedImage?.generated_image_url;
            if (url?.startsWith('data:')) {
              try {
                const r = await fetch(url);
                const blob = await r.blob();
                await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
                toast.success('Image copied to clipboard');
              } catch { toast.error('Copy failed — try downloading'); }
            }
          }} variant="outline" className="gap-2 h-10 text-xs" data-testid="tryon-copy-btn">
            <Copy className="w-4 h-4" /> Copy Image
          </Button>
          <Button onClick={handleDownload} variant="outline" className="gap-2 h-10 text-xs">
            <Download className="w-4 h-4" /> Download
          </Button>
          <Button onClick={() => setStep(2)} variant="outline" className="gap-2 h-10 text-xs">
            <RefreshCw className="w-4 h-4" /> Try Different
          </Button>
          <Button onClick={handleGenerate} disabled={generating} variant="outline" className="gap-2 h-10 text-xs col-span-2">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate Same Look
          </Button>
        </div>
        
        <Button onClick={onClose} variant="ghost" className="w-full">
          Close
        </Button>
      </div>
    );
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="sm:max-w-[420px] bg-white">
          <SheetHeader>
            <SheetTitle className="text-xl font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: '"Bodoni Moda", Georgia, serif' }}>
              {step === 4 ? 'Try-On Result' : 'Try On This Fabric'}
            </SheetTitle>
          </SheetHeader>
          
          <ScrollArea className="h-[calc(100vh-100px)] mt-4">
            <div className="pr-4">
              {/* Fabric preview */}
              {step < 4 && (
                <div className="mb-4 p-3 rounded-xl border border-border/60 flex items-center gap-3" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-border/60 relative">
                    {fabric.swatch_image_url ? (
                      <img
                        src={fabric.swatch_image_url.startsWith('/api') ? `${BACKEND_URL}${fabric.swatch_image_url}` : fabric.swatch_image_url}
                        alt={fabric.internal_code}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        <div className="w-full h-full" style={{ backgroundColor: fabric.colour_primary || '#D4C5A9' }} />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20" title="No swatch image - upload for best try-on results">
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--brand-navy)' }}>{fabric.internal_code}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--ink-muted)' }}>{fabric.mill_name}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--ink-muted)' }}>{fabric.composition}</p>
                    {!fabric.swatch_image_url && (
                      <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> No swatch image — results may vary
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Steps */}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      
      {/* Save to New Client Dialog */}
      <Dialog open={showSaveToClientDialog} onOpenChange={setShowSaveToClientDialog}>
        <DialogContent className="max-w-sm" data-testid="save-to-client-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--brand-navy)' }}>Save to New Client</DialogTitle>
          </DialogHeader>
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Create a new client and save this try-on image to their gallery.
          </p>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name *</Label>
                <Input
                  value={saveClientForm.first_name}
                  onChange={(e) => setSaveClientForm(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="John"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name *</Label>
                <Input
                  value={saveClientForm.last_name}
                  onChange={(e) => setSaveClientForm(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Smith"
                  className="h-9"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Phone *</Label>
              <Input
                value={saveClientForm.phone}
                onChange={(e) => setSaveClientForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+91 98765 43210"
                className="h-9"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowSaveToClientDialog(false)}>Cancel</Button>
            <Button 
              size="sm" 
              disabled={savingToClient} 
              onClick={handleSaveToNewClient}
              style={{ backgroundColor: 'var(--brand-gold)' }}
              data-testid="confirm-save-to-client-btn"
            >
              {savingToClient ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3 h-3 mr-1" />
                  Create & Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TryOnFabricPanel;
